import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { UseQueryOptions, UseQueryResult } from 'react-query';
import http from '../config/http-service';
import type { MergePreviewResult } from './types';
import { masterProductFamiliesEndpoint } from './use-get-master-product-families-api';

type TData = MergePreviewResult;
type TError = AxiosError;
type TQueryKey = [string, { sourceId?: number; targetFamilyId?: number }];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

// GET api/v1/cms/master-product-families/:sourceId/merge-preview?targetFamilyId=
// Dry-run for the merge modal: shows how many variants move and which
// collide with an existing variant on the target family before committing.
export function useMergeFamilyPreviewApi(sourceId?: number, targetFamilyId?: number, options?: Options): ReturnType {
  const isLoggedIn = useIsLoggedIn();
  // Destructure to compose caller enabled with built-in guards (never replace)
  const { enabled: callerEnabled, ...restOptions } = options ?? {};

  const queryFn = async () => {
    const { data } = await http.get(`${masterProductFamiliesEndpoint}/${sourceId}/merge-preview`, {
      params: { targetFamilyId },
    });
    return data?.data ?? data;
  };

  return useQueryWrapper([`${masterProductFamiliesEndpoint}/merge-preview`, { sourceId, targetFamilyId }], queryFn, {
    enabled: (callerEnabled ?? true) && isLoggedIn && sourceId != null && targetFamilyId != null,
    retry: false,
    ...restOptions,
  });
}
